'use client';

/**
 * Alt-click "pick through" — cycle through stacked items under the
 * pointer by repeated Alt-clicks.
 *
 * Figma uses Cmd-click for this gesture. We can't take Cmd on the
 * workspace canvas because Cmd-click is already wired to "force the
 * canvas context menu open over widget bodies" (see workspace-canvas
 * Cmd-click capture listener) and to RF's `multiSelectionKeyCode`.
 * Alt is unclaimed for pointer events — Alt-DRAG is the existing
 * "pan-through widgets" gesture, but Alt-CLICK on its own does
 * nothing today, which leaves room for pick-through to slot in
 * without breaking expectations.
 *
 * Conflict resolution with Alt-drag pan:
 *   - Track pointerdown coords + time.
 *   - On pointerup, if the pointer moved < 4px AND held for < 250ms,
 *     treat it as a CLICK and cycle. Otherwise the user was
 *     panning — ignore.
 *
 * Cycle semantics: the user clicks once → select the topmost item
 * under the cursor. Click again at (roughly) the same spot within
 * 600ms → select the next item DOWN the z-stack. Reset on pointer
 * move > 8px, on the timeout expiring, or on any non-Alt click.
 *
 * Inputs: positioned workspace items + a setter to swap the
 * controlled `selectedNodeIds` set in `workspace-canvas.tsx`. We
 * don't take selection state directly because the canvas already
 * owns it; threading the setter keeps the hook side-effect free
 * from the canvas's perspective.
 */

import { useEffect, useRef } from 'react';
import { useReactFlow } from '@xyflow/react';
import type { WorkspaceItemDTO } from '@/lib/workspaces/types';

interface UsePickThroughArgs {
  /** Live workspace items snapshot. */
  items: WorkspaceItemDTO[];
  /** Wrapper element — listeners are attached here. */
  wrapper: React.RefObject<HTMLElement | null>;
  /** Setter for the controlled node-selection set. */
  setSelectedNodeIds: (next: Set<string>) => void;
  /** Setter for the controlled edge-selection set (cleared on cycle). */
  setSelectedEdgeIds: (next: Set<string>) => void;
}

const CLICK_DISTANCE_PX = 4;
const CLICK_TIME_MS = 250;
const CYCLE_RESET_MS = 600;
const CYCLE_RESET_DISTANCE_PX = 8;

export function usePickThrough({
  items,
  wrapper,
  setSelectedNodeIds,
  setSelectedEdgeIds,
}: UsePickThroughArgs): void {
  const flow = useReactFlow();
  // Mirror the items array in a ref so the (one-shot) listener
  // always sees the latest snapshot without re-binding.
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Stash the press / cycle state.
  const press = useRef<{
    x: number;
    y: number;
    t: number;
  } | null>(null);
  const cycle = useRef<{
    x: number;
    y: number;
    t: number;
    stack: string[];
    index: number;
  } | null>(null);

  useEffect(() => {
    const el = wrapper.current;
    if (!el) return;

    const isPickGesture = (e: PointerEvent): boolean => {
      // Alt-only. We deliberately rule out Shift / Meta / Ctrl
      // combos so the gesture doesn't collide with multi-select
      // (Shift+Alt is a future "expand selection" affordance) or
      // with the Cmd-click context-menu override.
      if (!e.altKey) return false;
      if (e.shiftKey || e.metaKey || e.ctrlKey) return false;
      if (e.button !== 0) return false;
      const target = e.target as Element | null;
      // Limit to the actual canvas surface — chrome menus, panels,
      // and any element marked `data-canvas-chrome` keep their own
      // click semantics.
      if (!target) return false;
      if (target.closest('[data-canvas-chrome]')) return false;
      return true;
    };

    const onPointerDown = (e: PointerEvent) => {
      if (!isPickGesture(e)) {
        press.current = null;
        return;
      }
      press.current = { x: e.clientX, y: e.clientY, t: Date.now() };
    };

    const onPointerUp = (e: PointerEvent) => {
      const p = press.current;
      press.current = null;
      if (!p) return;
      if (!isPickGesture(e)) return;
      const dt = Date.now() - p.t;
      const dx = e.clientX - p.x;
      const dy = e.clientY - p.y;
      if (dt > CLICK_TIME_MS) return;
      if (dx * dx + dy * dy > CLICK_DISTANCE_PX * CLICK_DISTANCE_PX) return;

      // Resolve the stack of positioned items under the click in
      // FLOW coordinates so widget bounds line up regardless of
      // the current zoom.
      const pt = flow.screenToFlowPosition({ x: e.clientX, y: e.clientY });
      const stack = stackUnder(itemsRef.current, pt);
      if (stack.length === 0) {
        cycle.current = null;
        return;
      }

      // Decide whether we're continuing an existing cycle or
      // starting fresh. We require the pointer to be near the
      // previous click AND within the timeout, AND for the
      // candidate stack to match the previous one (so a click
      // that opened a new combination doesn't accidentally inherit
      // the prior cycle index).
      const now = Date.now();
      let nextIndex = 0;
      const prev = cycle.current;
      if (
        prev &&
        now - prev.t < CYCLE_RESET_MS &&
        Math.hypot(e.clientX - prev.x, e.clientY - prev.y) <
          CYCLE_RESET_DISTANCE_PX &&
        sameStack(prev.stack, stack)
      ) {
        nextIndex = (prev.index + 1) % stack.length;
      }
      cycle.current = {
        x: e.clientX,
        y: e.clientY,
        t: now,
        stack,
        index: nextIndex,
      };
      const id = stack[nextIndex];
      // Suppress the underlying click handlers so the gesture
      // doesn't double-fire as both a pick-through AND a regular
      // widget click (which would re-select the topmost item and
      // undo the cycle).
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      setSelectedNodeIds(new Set([id]));
      setSelectedEdgeIds(new Set());

      // CRITICAL: a synthetic `click` event follows every
      // `pointerdown` → `pointerup` pair. React Flow's `onPaneClick`
      // listens for that synthetic click and calls
      // `setSelectedNodeIds(new Set())` — which would wipe the
      // selection we just set above one tick later. Install a
      // one-shot capture-phase click listener that swallows the
      // very next click so the pane-clear logic never sees it.
      // Auto-clears on a 500ms timer in case the click never
      // arrives (rare but possible if a parent calls
      // preventDefault on pointerdown).
      const swallow = (ev: MouseEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
        ev.stopImmediatePropagation();
        window.removeEventListener('click', swallow, true);
        document.removeEventListener('click', swallow, true);
        clearTimeout(timer);
      };
      const timer = window.setTimeout(() => {
        window.removeEventListener('click', swallow, true);
        document.removeEventListener('click', swallow, true);
      }, 500);
      window.addEventListener('click', swallow, true);
      document.addEventListener('click', swallow, true);
    };

    el.addEventListener('pointerdown', onPointerDown, true);
    el.addEventListener('pointerup', onPointerUp, true);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown, true);
      el.removeEventListener('pointerup', onPointerUp, true);
    };
  }, [flow, wrapper, setSelectedEdgeIds, setSelectedNodeIds]);
}

/**
 * Return ids of items whose AABB contains `pt`, sorted top-to-bottom
 * (highest `zIndex` first). Docked items + connectors are skipped —
 * they don't have a screen bbox. Exported so unit tests can poke at
 * the math without spinning up React.
 */
export function stackUnder(
  items: WorkspaceItemDTO[],
  pt: { x: number; y: number },
): string[] {
  const hits: { id: string; z: number }[] = [];
  for (const it of items) {
    if (it.state.docked) continue;
    if (it.type === 'connector') continue;
    const { x, y, width, height } = it.state;
    if (pt.x < x || pt.x > x + width) continue;
    if (pt.y < y || pt.y > y + height) continue;
    hits.push({ id: it.id, z: it.state.zIndex ?? 0 });
  }
  hits.sort((a, b) => b.z - a.z);
  return hits.map((h) => h.id);
}

function sameStack(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
